import { ReactElement, useCallback } from 'react'
import { useCommentMutations } from '@scene'
import { Stack, Typography } from '@mui/material'
import { Form2 } from '@ui/forms'
import { useElementLabel, useJumpToResultElement } from '@editorHooks'
import CommentSettingsFields from './CommentSettingsFields'
import LinkedCommentInfo from './LinkedCommentInfo'
import {
  CommentSettingsFormValues,
  commentToFormValues,
  formValuesToEditedFields,
} from './commentSettingsForm'

interface Props {
  comment: Comment3D
}

/**
 * Editing panel for a single comment, shown in the CommentDrawer. Mirrors the in-scene
 * CommentEditPopup's settings, but persists each change immediately via Form2's `submitOnChange`
 * (the drawer stays open, so there is no save-on-close moment). Mounted with a `key` of the comment
 * id (see CommentDrawer) so the form re-seeds when a different comment is selected.
 */
const CommentSettingsPanel = ({ comment }: Props): ReactElement => {
  const { updateCommentFields } = useCommentMutations()
  const jumpToResultElement = useJumpToResultElement()
  const getLabel = useElementLabel()

  // Element-linked comments: their text and type are owned by the position list, so the type stays
  // locked here (as in the scene popup). They gain a "jump to element" action instead.
  const linkedElementGuid = comment.element_guid ?? null
  const isLinked = !!linkedElementGuid

  const handleSubmit = useCallback(
    (values: CommentSettingsFormValues) =>
      updateCommentFields(comment.id, formValuesToEditedFields(values)),
    [comment.id, updateCommentFields],
  )

  return (
    <Form2
      defaultValues={commentToFormValues(comment)}
      onSubmit={handleSubmit}
      submitOnChange
      data-cy="comment-settings-form"
      style={{ padding: 12 }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Kommentar bearbeiten</Typography>

        {linkedElementGuid && (
          <LinkedCommentInfo
            label={getLabel(linkedElementGuid)}
            onJump={() => jumpToResultElement(linkedElementGuid)}
          />
        )}

        <CommentSettingsFields allowCustomType={comment.type === 'custom'} disableType={isLinked} />
      </Stack>
    </Form2>
  )
}

export default CommentSettingsPanel
